// src/pages/TestManagementPage/CreateTestTab.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Form,
  Input,
  InputNumber,
  Switch,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Select,
  Table,
  App,
  Modal,
  Progress,
  Tooltip,
  message as antdMessage,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getChaptersByLevel, getExerciseDetailsById } from '../../services/exerciseService';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase-config';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/* ========== styles ========== */
const twoLineClamp = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};
const linkishButton = {
  padding: 0,
  height: 'auto',
  textAlign: 'left',
  display: 'inline-block',
  width: '100%',
};
const nowrap = { whiteSpace: 'nowrap' };

/* parts → plain text（2行省略用） */
const partsToPlainText = (parts) => {
  if (!Array.isArray(parts)) return '';
  return parts.map((p) => p.text || p.value).join('');
};

/* ルビ対応の全文表示 */
const renderFullContent = (parts) => {
  if (!Array.isArray(parts)) return <Paragraph>内容がありません。</Paragraph>;
  return (
    <Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
      {parts.map((part, i) =>
        part.type === 'ruby' ? (
          <ruby key={i}>
            {part.text}
            <rt>{part.ruby}</rt>
          </ruby>
        ) : (
          <span key={i}>{part.value}</span>
        ),
      )}
    </Paragraph>
  );
};

const CreateTestTab = () => {
  const [form] = Form.useForm();

  // AntD context + fallback
  const appCtx = App.useApp?.();
  const message = appCtx?.message ?? antdMessage;
  const modal = appCtx?.modal ?? Modal;

  const { currentUser } = useAuth();
  const userId = currentUser?.id || currentUser?.uid || null;

  /* ===== state ===== */
  const [saving, setSaving] = useState(false);
  const [isTimed, setIsTimed] = useState(false);
  const [isAvailabilitySet, setIsAvailabilitySet] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);

  const [availableExercises, setAvailableExercises] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // 問題IDごとの集計: { [id]: { attempts, correct, wrong, wrongPercent } }
  const [globalStats, setGlobalStats] = useState({});

  // 内容モーダル
  const [isContentModalVisible, setIsContentModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState('');

  /* ===== レベル変更時：問題取得 ===== */
  useEffect(() => {
    if (!selectedLevel) {
      setAvailableExercises([]);
      setSelectedExercises([]);
      setGlobalStats({});
      return;
    }
    const chapters = getChaptersByLevel(selectedLevel);
    const list = chapters.flatMap((ch) =>
      ch.exercises.map((ex) => {
        const details = getExerciseDetailsById(ex.id);
        return { ...ex, content: details?.questions?.[0]?.content };
      }),
    );
    setAvailableExercises(list);
    setSelectedExercises([]);
    setPagination((p) => ({ ...p, current: 1 }));
  }, [selectedLevel]);

  /* ===== 誤答率（全ユーザー集計）を読み込み ===== */
  useEffect(() => {
    const fetchGlobal = async () => {
      try {
        const ids = availableExercises.map((e) => e.id);
        if (ids.length === 0) {
          setGlobalStats({});
          return;
        }

        // Firestore where 'in' は最大10要素 → 分割
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

        const progressRef = collection(db, 'progress');
        const agg = {}; // { id: { attempts, correct } }

        for (const c of chunks) {
          const qy = query(progressRef, where('exerciseId', 'in', c));
          const snap = await getDocs(qy);
          snap.docs.forEach((d) => {
            const data = d.data();
            const exId = data.exerciseId;
            if (!exId) return;

            const attempts = Number(data.attempts ?? 0);
            const correct = Number(data.completedCount ?? 0);

            if (!agg[exId]) agg[exId] = { attempts: 0, correct: 0 };
            agg[exId].attempts += attempts > 0 ? attempts : 0;
            agg[exId].correct += Math.min(correct, attempts);
          });
        }

        const result = {};
        Object.keys(agg).forEach((exId) => {
          const a = agg[exId].attempts;
          const c = agg[exId].correct;
          const w = Math.max(a - c, 0);
          const wrongPercent = a > 0 ? Math.round((w / a) * 100) : 0;
          result[exId] = { attempts: a, correct: c, wrong: w, wrongPercent };
        });

        setGlobalStats(result);
      } catch (err) {
        console.error('fetchGlobal error:', err);
        setGlobalStats({});
      }
    };

    fetchGlobal();
  }, [availableExercises]);

  /* ===== 合計点 ===== */
  const totalScore = useMemo(
    () => selectedExercises.reduce((sum, ex) => sum + (ex.points || 0), 0),
    [selectedExercises],
  );

  /* ===== 左表の選択 ===== */
  const rowSelection = {
    onChange: (_keys, rows) => {
      const merged = rows.map((row) => {
        const old = selectedExercises.find((ex) => ex.id === row.id);
        return old || { ...row, points: 10 };
      });
      setSelectedExercises(merged);
    },
    selectedRowKeys: selectedExercises.map((ex) => ex.id),
  };

  const handlePointsChange = (exerciseId, points) => {
    setSelectedExercises((prev) =>
      prev.map((ex) => (ex.id === exerciseId ? { ...ex, points: points || 0 } : ex)),
    );
  };

  const handleTableChange = (newPg) => setPagination(newPg);

  const showContentModal = (record) => {
    setModalTitle(`問題内容: ${record.title}`);
    setModalContent(record.content);
    setIsContentModalVisible(true);
  };

  const handleModalClose = () => setIsContentModalVisible(false);

  /* ===== テスト名重複チェック ===== */
  const isTestNameExists = async (name) => {
    const testsRef = collection(db, 'tests');
    const q = query(testsRef, where('testName', '==', name));
    const qs = await getDocs(q);
    return !qs.empty;
  };

  /* ===== 送信 ===== */
  const onFinish = async (values) => {
    if (!selectedLevel) {
      message.error('レベルを選択してください。');
      return;
    }
    if (selectedExercises.length === 0) {
      message.error('少なくとも1問を選択してください。');
      return;
    }

    const trimmedName = (values.testName || '').trim();

    // 事前に重複チェック
    if (await isTestNameExists(trimmedName)) {
      message.error('このテスト名は既に存在します。');
      return;
    }

    modal.confirm({
      title: 'テスト作成の確認',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p><strong>テスト名:</strong> {trimmedName}</p>
          <p><strong>問題数:</strong> {selectedExercises.length}</p>
          <p><strong>合計点:</strong> {totalScore}</p>
        </div>
      ),
      okText: '作成',
      cancelText: 'キャンセル',
      onOk: async () => {
        setSaving(true);
        try {
          const testsRef = collection(db, 'tests');
          const { availabilityRange, isTimed: fIsTimed, timeLimit, ...rest } = values;

          const payload = {
            ...rest,
            testName: trimmedName,
            levelId: selectedLevel,
            isTimed: !!fIsTimed,
            timeLimit: fIsTimed ? Number(timeLimit ?? 0) : null,
            startTime: availabilityRange?.[0] ? availabilityRange[0].toDate() : null,
            endTime: availabilityRange?.[1] ? availabilityRange[1].toDate() : null,
            questions: selectedExercises.map(({ id, points }) => ({
              exerciseId: id,
              points: points || 0,
            })),
            totalScore,
            createdBy: userId,
            createdAt: serverTimestamp(),
          };

          await addDoc(testsRef, payload);

          message.success('テストを作成しました。フォームをリセットしました。');

          // リセット
          form.resetFields();
          setIsTimed(false);
          setIsAvailabilitySet(false);
          setSelectedLevel(null);
          setAvailableExercises([]);
          setSelectedExercises([]);
          setGlobalStats({});
          setPagination({ current: 1, pageSize: 10 });

          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
          console.error(err);
          message.error('保存中にエラーが発生しました。');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const onFinishFailed = ({ errorFields }) => {
    form.scrollToField(errorFields?.[0]?.name || []);
    message.error('必須項目を確認してください。');
  };

  /* ===== 列定義 ===== */
  const availableColumns = [
    {
      title: '問題ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      align: 'center',
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '問題名',
      dataIndex: 'title',
      key: 'title',
      width: 160,
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '誤答率(%)',
      key: 'globalWrong',
      width: 120,
      align: 'center',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (globalStats[a.id]?.wrongPercent || 0) - (globalStats[b.id]?.wrongPercent || 0),
      render: (_, record) => {
        const st = globalStats[record.id] || { wrongPercent: 0, attempts: 0, wrong: 0 };
        const tip = `誤答 ${st.wrong}/${st.attempts} 回`;
        return (
          <Tooltip title={tip}>
            <div style={{ minWidth: 110 }}>
              <Progress percent={st.wrongPercent} size="small" />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      render: (content, record) => {
        const fullText = partsToPlainText(content?.parts);
        return (
          <Button type="link" onClick={() => showContentModal(record)} style={linkishButton}>
            <span style={twoLineClamp} title={fullText}>
              {fullText}
            </span>
          </Button>
        );
      },
    },
  ];

  const selectedColumns = [
    {
      title: '問題ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '問題名',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (v) => <span style={nowrap}>{v}</span>,
    },
    {
      title: '点数',
      dataIndex: 'points',
      key: 'points',
      width: 120,
      align: 'center',
      render: (points, record) => (
        <InputNumber min={0} value={points} onChange={(v) => handlePointsChange(record.id, v)} disabled={saving} />
      ),
    },
  ];

  /* ===== UI ===== */
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      scrollToFirstError
      initialValues={{ isTimed: false, isAvailabilitySet: false, timeLimit: 60 }}
    >
      {/* 基本情報 */}
      <Card title="基本情報" styles={{ body: { paddingBottom: 8 } }}>
        <Row gutter={[24, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="testName"
              label="テスト名"
              rules={[
                { required: true, message: 'テスト名を入力してください。' },
                { whitespace: true, message: '空白のみは無効です。' },
              ]}
            >
              <Input placeholder="例：期末試験（レベル1）" disabled={saving} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="levelId"
              label="レベルを選択"
              rules={[{ required: true, message: 'レベルを選択してください。' }]}
            >
              <Select
                placeholder="レベルを選択"
                onChange={(v) => {
                  setSelectedLevel(v);
                  if (form.getFieldValue('levelId') !== v) form.setFieldsValue({ levelId: v });
                }}
                disabled={saving}
                allowClear
              >
                <Option value="level1">簿記3級</Option>
                <Option value="level2">簿記2級</Option>
                <Option value="level3">日商簿記</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="isTimed" label="時間制限" valuePropName="checked">
              <Switch onChange={setIsTimed} disabled={saving} />
            </Form.Item>
            {isTimed && (
              <Form.Item
                name="timeLimit"
                label="制限時間（分）"
                rules={[{ required: true, message: '制限時間を入力してください。' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} disabled={saving} />
              </Form.Item>
            )}
          </Col>

          <Col xs={24} md={12}>
            <Form.Item name="isAvailabilitySet" label="有効期間を設定" valuePropName="checked">
              <Switch onChange={setIsAvailabilitySet} disabled={saving} />
            </Form.Item>
            {isAvailabilitySet && (
              <Form.Item name="availabilityRange" label="有効期間（開始〜終了）">
                <RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} disabled={saving} />
              </Form.Item>
            )}
          </Col>
        </Row>
      </Card>

      {/* 問題選択 */}
      <Card title="問題選択" style={{ marginTop: 16 }}>
        {!selectedLevel ? (
          <Text type="secondary">レベルを選択すると問題リストを読み込みます。</Text>
        ) : (
          <Row gutter={[24, 16]}>
            <Col xs={24} lg={12}>
              <Title level={4} style={{ marginBottom: 12 }}>
                利用可能な問題
              </Title>
              <Table
                rowKey="id"
                size="middle"
                rowSelection={rowSelection}
                columns={availableColumns}
                dataSource={availableExercises}
                bordered
                tableLayout="auto"
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
              />
            </Col>

            <Col xs={24} lg={12}>
              <Title level={4} style={{ marginBottom: 12 }}>
                選択した問題
              </Title>
              <Table
                rowKey="id"
                size="middle"
                columns={selectedColumns}
                dataSource={selectedExercises}
                bordered
                tableLayout="auto"
                pagination={{ pageSize: pagination.pageSize }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>合計点</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{totalScore}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Col>
          </Row>
        )}
      </Card>

      {/* 送信ボタン */}
      <Form.Item style={{ marginTop: 20, textAlign: 'right' }}>
        <Button type="primary" htmlType="submit" loading={saving}>
          作成
        </Button>
      </Form.Item>

      {/* 内容プレビュー用モーダル */}
      <Modal
        title={modalTitle}
        open={isContentModalVisible}
        onCancel={handleModalClose}
        footer={[<Button key="close" onClick={handleModalClose}>閉じる</Button>]}
        width={800}
        centered
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 10 }}>
          {modalContent ? renderFullContent(modalContent.parts) : <Text>内容がありません。</Text>}
        </div>
      </Modal>
    </Form>
  );
};

export default CreateTestTab;
